import { Controller, useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { startOfDay } from "date-fns"
import { kebabCase } from "es-toolkit/string"
import { RadioGroup, RadioGroupItem, RadioGroupLabel } from "@/components/ui/radio-group"
import { cn } from "@/lib/utils"
import { TimeWindowReferenceDatePicker } from "@/modules/patients/agenda/time-window/components/time-window-reference-date-picker"
import { ErrorCorrection, TimeWindowFormSchema } from "@/modules/patients/agenda/time-window/time-window-schema"

interface ErrorCorrectionDetail {
  label: string
  value: ErrorCorrection
}

interface TimeWindowErrorCorrectionProps {
  isViewMode: boolean
}

const TimeWindowErrorCorrection: React.FC<TimeWindowErrorCorrectionProps> = ({ isViewMode }) => {
  const { t } = useTranslation()

  const list: ErrorCorrectionDetail[] = [
    {
      label: "patient.agenda.timeWindow",
      value: "timeWindow",
    },
    {
      label: "patient.agenda.timeWindowErrorCorrection",
      value: "timeWindowErrorCorrection",
    },
  ]

  const { control, resetField, setError } = useFormContext<TimeWindowFormSchema>()

  return (
    <Controller
      name="correction"
      control={control}
      render={({ field: { value, onChange } }) => (
        <>
          <RadioGroup
            value={value}
            onValueChange={(e: ErrorCorrection) => {
              onChange(e)
              resetField("dateTo")
              resetField("dateFrom")
              resetField("referralReason")
            }}
            className={cn("grid-flow-col justify-start gap-2")}>
            {list.map((item) => (
              <div key={item.label}>
                <RadioGroupItem
                  data-testid={`${kebabCase(item.value)}-button`}
                  className="w-auto"
                  value={item.value}
                  id={item.value}
                  disabled={isViewMode}>
                  <RadioGroupLabel htmlFor={item.value}>{t(item.label)}</RadioGroupLabel>
                </RadioGroupItem>
              </div>
            ))}
          </RadioGroup>
          {value === "timeWindowErrorCorrection" && (
            <Controller
              name="referenceDate"
              defaultValue={startOfDay(new Date())}
              control={control}
              render={({ field: { value: referenceDate, onChange }, fieldState: { error } }) => {
                const currentDate = referenceDate ? new Date(referenceDate) : startOfDay(new Date())
                return (
                  <div className="relative max-w-44.5">
                    <TimeWindowReferenceDatePicker
                      onSelect={onChange}
                      value={currentDate}
                      error={error?.message}
                      setError={(message) => setError("referenceDate", { type: "custom", message })}
                      onClear={() => onChange(undefined)}
                      isViewMode={isViewMode}
                    />
                  </div>
                )
              }}
            />
          )}
        </>
      )}
    />
  )
}

export { TimeWindowErrorCorrection }
